// src/portal-app/team/RolesScreen.js
//
// Plan 4 Task 20 — Roles + Users screen, migrated to the P4 pattern
// (spec §5). operations/TransactionsScreen.js:32-55 is the reference
// implementation; operations/ReservationsScreen.js (Task 7) and
// team/EmployeesScreen.js (Task 9) are the closest reviewed models — a
// two-source SegmentedControl (Reservations) and a single-fetch,
// client-side-filtered CondensedRow list with a FAB (Employees). This
// header records only what is specific to Roles.
//
// THIS SCREEN IS THE PERMISSION EDITOR. Its route id ('roles') and its
// hub-card permission gate ('portal:roles', src/portal-app/nav/tabs.js)
// are UNCHANGED — Plan 2 only moved the tab this card lives under (Team ->
// More), the screen itself was never migrated until now, and this task
// does not touch either identifier. See the task report's permission-gate
// section for the mutation evidence on every guard this file carries.
//
// KNOWN GAP (not fixed here, out of scope — same finding Task 18 made for
// payroll): RolesScreen.js has no fine-grained in-component permission
// check of its own. Access is gated only by the coarse hub card
// (`permission: 'portal:roles'` in nav/tabs.js) and by nav/routes.js
// registering the route at all — nothing inside this component calls
// hasPermission('portal:roles'). Reported in the task report; fixing the
// app's gating architecture is a cross-screen change beyond one task.
//
// FILE SPLIT (spec §7, same boundary Task 7 established for
// ReservationsScreen.js) — done in ONE COMMIT with the P4 migration
// (b61bf97), NOT as a separate mechanical commit first the way Task 7 did
// it. That was a deliberate call, not an oversight, and it does not apply
// uniformly to all four extracted files — see each file's own header for
// its exact provenance:
//   rolesShared.js           CSS + avatar helpers + icons + useEscape —
//                            icons/useEscape/initials() are a VERBATIM
//                            move; the CSS is NOT (extended for the P4
//                            layout, dead pre-migration rules dropped —
//                            see that file's header). Diffable against
//                            `git show b61bf97^:frontend/src/portal-app/team/RolesScreen.js`.
//   RoleForms.js             every write path (role form, user form,
//                            reset-password form) — VERBATIM move, diffable
//                            against
//                            `git show b61bf97^:frontend/src/portal-app/team/RolesScreen.js`.
//   RolePermissionEditor.js  the permission grid — VERBATIM move (only the
//                            `function` -> `export function` keyword and a
//                            name change), diffable against
//                            `git show b61bf97^:frontend/src/portal-app/team/RolesScreen.js`.
//   RoleRows.js              the read path — NOT verbatim. RoleRow is a
//                            ground-up rewrite onto CondensedRow (brief:
//                            "Rows: CondensedRow per role"); there was no
//                            byte-identical intermediate form of the
//                            pre-migration role card to extract first, so
//                            a separate "extraction, tests still green"
//                            commit was not possible for this one file the
//                            way it was for the other three. UserRow is a
//                            near-verbatim move (see RoleRows.js header for
//                            its one deliberate change — the avatar
//                            palette).
//   RolesScreen.js           state, fetching and layout only (this file) —
//                            NOT verbatim; this is where the P4 layout
//                            (hero, control row, FilterSheet, PullToRefresh)
//                            was built.
// In hindsight RoleForms.js and RolePermissionEditor.js — the two files
// that ARE fully verbatim moves — could have been their own mechanical
// commit ahead of RoleRows/RolesScreen's rewrite. The single-commit call
// was made for all four files together, which was broader than it needed
// to be for those two. Recorded here so an auditor trusts a "verbatim"
// claim only where a specific file's header makes it explicitly, not by
// assuming it from this summary.
//
// SEGMENTED CONTROL — VERIFIED as a genuine sibling-view switch, not a
// categorical filter in disguise (the defect a Task 4 review caught on
// Products' Active/Inactive toggle). Roles hits GET /admin/roles
// (roleService.getRoles); Users hits GET /admin/tenant-credentials
// (roleService.getCredentials) — two different endpoints
// (backend/app/routers/roles.py:46,233), two different response shapes
// (Role: id/name/description/is_default/user_count vs Credential:
// id/username/display_name/role_id/is_active/short_id), two different row
// renderers (RoleRow vs UserRow). Both ARE fetched together in one
// Promise.all inside load() — that part is UNCHANGED from pre-migration
// (see "fetch timing" below) — but "fetched together" is not "the same
// array filtered two ways": nothing about switching segments re-filters a
// shared list, which is the actual pattern this rule guards against.
//
// TWO FilterRows (Users tab: role, status) -> ONE FilterSheet behind ONE
// FilterButton, per the brief. The Roles tab carries no filters at all (no
// pre-migration FilterRow existed for it), so it renders no control row.
//
// HERO — Roles tab only (brief: "role count, with the count of users
// assigned as secondary"), covers exactly the CondensedRow list rendered
// below it: value is roles.length (the array the list maps over), caption
// is the sum of user_count across that same array. The Users tab
// deliberately gets NO hero — same "tab-scoped, never mixed" rule
// Expenses' daily/overhead heroes and Reservations' two heroes both
// follow; there is nothing in the brief calling for a Users-tab hero, and
// a headcount hero there would just restate the count line beneath it.
//
// GENUINE EMPTY ON FILTER (Users tab; EmployeesScreen.js Task 9 precedent,
// not this task's invention). The combined `status` state — unchanged from
// pre-migration — flips to 'empty' only when BOTH roles and credentials
// come back empty (`r.length > 0 || c.length > 0 ? 'ready' : 'empty'`),
// which is a genuine raw-fetch state and is used as-is for the Roles tab
// surface. For the Users tab, `usersSurfaceStatus` is separately derived
// from the POST-FILTER array (`status === 'ready' && filtered.length === 0
// ? 'empty' : status`), so a search term or role/status filter that
// matches nobody really does flip SurfaceStates into its empty branch —
// see the task report's mutation-evidence section for the controls-outside-
// SurfaceStates proof this enables.
//
// FETCH TIMING IS UNCHANGED. One load() on mount, fetching roles and
// credentials together via Promise.all — exactly the pre-migration
// behaviour. No new eager fetch was added to feed the hero (it's a pure
// reduction over the already-fetched `roles` array) or the FilterSheet
// groups (role options are derived from the same `roles` array).
//
// FAB — "Add Role" was an inline dashed button INSIDE the (then combined)
// SurfaceStates body; "Add User" was already a fixed FAB. Both are now
// FABs rendered OUTSIDE SurfaceStates/PullToRefresh, at the same position,
// swapped by segment — so "Add Role" is reachable even when the roles list
// is empty (P4 recipe: a screen's one create action must survive its own
// empty state), matching "Add User"'s pre-existing placement instead of
// the other way around.
//
// LIVE BUG FOUND + FIXED (frontend/src/portal-app scope only — no service-
// layer file touched): the create-credential payload never included
// `entity_id`. backend/app/routers/roles.py:247's TenantCredentialCreate
// requires it with no default (it is a locally-defined Pydantic model in
// that router file, NOT imported from app/models/models.py — roles.py:10's
// import line does not include it), so POST /admin/tenant-credentials
// without it 422s at roles.py:266-274 — every "Add User" on this screen was
// broken. Verified against
// the web portal's equivalent call, which already sends
// `entity_id: tenantId` (frontend/src/pages/portal/PortalRolesPage.js:882).
// Fixed at the RolesScreen.js call site (handleSaveUser), matching every
// other mutation on this screen, which injects tenantId at the
// orchestration layer rather than inside the sheet.
//
// Dropped fields (flagged per Lesson 5): role.description is dropped from
// the row — the brief's field list ("role name, assigned-user count as
// secondary, StatusBadge") is explicit and does not include it; see
// RoleRows.js's file header for the full field mapping and the
// EmployeesScreen.js precedent this mirrors.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, {
    useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { roleService } from '../../services/api';
import { SurfaceStates } from '../insights/kit';
import {
    SegmentedControl,
    SearchInput,
    ConfirmSheet,
    FilterButton,
    FilterSheet,
    HeroStat,
    PullToRefresh,
    ensureKitCss,
    IconPlus,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';
import { useNavChrome } from '../nav/NavStack';
import { ensureRlCss } from './rolesShared';
import { RoleFormSheet, UserFormSheet, ResetPasswordSheet } from './RoleForms';
import { RolePermissionEditor } from './RolePermissionEditor';
import { RoleRow, UserRow } from './RoleRows';

/* ------------------------------------------------------------------ */
/* RolesScreen — main export                                          */
/* ------------------------------------------------------------------ */
export default function RolesScreen() {
    ensureRlCss();
    ensureKitCss();
    const { tenantId, username: currentUsername } = useAuth();
    const { t, isRTL } = useLanguage();
    useNavChrome(null, []);

    const [segment, setSegment] = useState('roles');
    const [roles, setRoles] = useState([]);
    const [credentials, setCredentials] = useState([]);
    const [status, setStatus] = useState('loading');
    const fetchRef = useRef(0);

    const load = useCallback(async () => {
        const id = ++fetchRef.current;
        setStatus('loading');
        try {
            const [rolesData, credsData] = await Promise.all([
                roleService.getRoles(tenantId),
                roleService.getCredentials(tenantId),
            ]);
            if (id !== fetchRef.current) return;
            const r = Array.isArray(rolesData) ? rolesData : [];
            const c = Array.isArray(credsData) ? credsData : [];
            setRoles(r);
            setCredentials(c);
            setStatus(r.length > 0 || c.length > 0 ? 'ready' : 'empty');
        } catch {
            if (id !== fetchRef.current) return;
            setStatus('error');
        }
    }, [tenantId]);

    useEffect(() => { load(); }, [load]);

    // ── Roles tab state ─────────────────────────────────────────────────
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [roleForm, setRoleForm] = useState({ open: false, initial: null });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, role: null });
    const [deletingRole, setDeletingRole] = useState(false);

    const handleSelectRole = (role) => {
        setSelectedRoleId((prev) => (prev === role.id ? null : role.id));
    };
    const openCreateRole = () => setRoleForm({ open: true, initial: null });
    const openEditRole = (role) => setRoleForm({ open: true, initial: role });
    const closeRoleForm = () => setRoleForm({ open: false, initial: null });

    const handleSaveRole = async (data) => {
        if (roleForm.initial) {
            await roleService.updateRole(roleForm.initial.id, tenantId, data);
        } else {
            await roleService.createRole(tenantId, data);
        }
        closeRoleForm();
        load();
    };

    const openDeleteRole = (role) => setDeleteConfirm({ open: true, role });
    const closeDeleteRole = () => setDeleteConfirm({ open: false, role: null });

    const handleDeleteRole = async () => {
        if (!deleteConfirm.role) return;
        setDeletingRole(true);
        try {
            await roleService.deleteRole(deleteConfirm.role.id, tenantId);
            const deletedId = deleteConfirm.role.id;
            closeDeleteRole();
            setSelectedRoleId((prev) => (prev === deletedId ? null : prev));
            load();
        } catch {
            // swallow — matches pre-migration behaviour
        }
        setDeletingRole(false);
    };

    // ── Users tab state ─────────────────────────────────────────────────
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    const [userForm, setUserForm] = useState({ open: false, mode: 'create', cred: null });
    const [resetSheet, setResetSheet] = useState({ open: false, credId: null });
    const [deactivatingId, setDeactivatingId] = useState(null);

    const roleMap = useMemo(
        () => Object.fromEntries(roles.map((r) => [r.id, r.name])),
        [roles],
    );

    const q = search.toLowerCase().trim();
    const filteredCredentials = useMemo(() => credentials.filter((c) => {
        if (roleFilter && String(c.role_id) !== String(roleFilter)) return false;
        if (statusFilter === 'active' && !c.is_active) return false;
        if (statusFilter === 'inactive' && c.is_active) return false;
        if (q) {
            const nameMatch = (c.display_name || '').toLowerCase().includes(q);
            const userMatch = (c.username || '').toLowerCase().includes(q);
            if (!nameMatch && !userMatch) return false;
        }
        return true;
    }), [credentials, roleFilter, statusFilter, q]);

    // A filter/search that matches nobody genuinely flips SurfaceStates into
    // its empty branch for the Users tab — see file header.
    const usersSurfaceStatus = status === 'ready' && filteredCredentials.length === 0 ? 'empty' : status;
    const isRoles = segment === 'roles';
    const surfaceStatus = isRoles ? status : usersSurfaceStatus;

    const roleFilterOptions = useMemo(
        () => roles.map((r) => ({ id: r.id, label: r.name })),
        [roles],
    );
    const statusFilterOptions = [
        { id: 'active', label: t('active', {}, 'Active') },
        { id: 'inactive', label: t('inactive', {}, 'Inactive') },
    ];
    const userFilterGroups = useMemo(() => {
        const groups = [];
        if (roleFilterOptions.length > 0) {
            groups.push({ id: 'role', label: t('role', {}, 'Role'), options: roleFilterOptions });
        }
        groups.push({ id: 'status', label: t('status', {}, 'Status'), options: statusFilterOptions });
        return groups;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleFilterOptions, t]);
    const userFilterValue = { role: roleFilter, status: statusFilter };
    const activeUserFilterCount = [roleFilter, statusFilter].filter((v) => v != null).length;
    const activeUserFilterText = [
        roleFilter != null ? (roleMap[roleFilter] || String(roleFilter)) : null,
        statusFilter === 'active' ? t('active', {}, 'Active') : (statusFilter === 'inactive' ? t('inactive', {}, 'Inactive') : null),
    ].filter(Boolean).join(' · ');

    const handleUserFilterChange = (groupId, optionId) => {
        if (groupId === 'role') setRoleFilter(optionId);
        else if (groupId === 'status') setStatusFilter(optionId);
    };
    const clearUserFilters = () => {
        setRoleFilter(null);
        setStatusFilter(null);
        setSearch('');
    };

    const openCreateUser = () => setUserForm({ open: true, mode: 'create', cred: null });
    const openEditUser = (cred) => setUserForm({ open: true, mode: 'edit', cred });
    const closeUserForm = () => setUserForm({ open: false, mode: 'create', cred: null });

    const handleSaveUser = async (payload) => {
        if (userForm.mode === 'create') {
            // See file header "LIVE BUG FOUND + FIXED": backend requires
            // entity_id on create (TenantCredentialCreate has no default),
            // and the pre-migration payload never sent it.
            await roleService.createCredential(tenantId, { ...payload, entity_id: tenantId });
        } else if (userForm.cred) {
            await roleService.updateCredential(userForm.cred.id, tenantId, payload);
        }
        closeUserForm();
        load();
    };

    const openResetPassword = (credId) => setResetSheet({ open: true, credId });
    const closeResetPassword = () => setResetSheet({ open: false, credId: null });
    const handleResetPassword = async (newPassword) => {
        await roleService.resetPassword(resetSheet.credId, tenantId, newPassword);
        closeResetPassword();
    };

    const handleToggleActive = async (cred) => {
        setDeactivatingId(cred.id);
        try {
            await roleService.updateCredential(cred.id, tenantId, { is_active: !cred.is_active });
            load();
        } catch {
            // swallow — matches pre-migration behaviour
        }
        setDeactivatingId(null);
    };

    // ── Derived: Roles-tab hero — same denominator as the rows below it ──
    const assignedUsersCount = useMemo(
        () => roles.reduce((sum, r) => sum + (Number(r.user_count) || 0), 0),
        [roles],
    );

    // ── Segment options ───────────────────────────────────────────────
    const segOptions = [
        { id: 'roles', label: t('roles', {}, 'Roles') },
        { id: 'users', label: t('users', {}, 'Users') },
    ];

    // Pull-to-refresh is suppressed whenever a sheet or overlay owns the
    // screen, matching Reservations/Inventory/Expenses.
    const ptrDisabled = roleForm.open
        || deleteConfirm.open
        || userForm.open
        || resetSheet.open
        || filterSheetOpen;

    const usersEmptyTitle = q || activeUserFilterCount > 0
        ? t('no_users_match', {}, 'No users match filters')
        : t('no_users_yet', {}, 'No users yet');
    const usersEmptyMsg = q || activeUserFilterCount > 0
        ? t('try_adjusting_search_or_filter', {}, 'Try adjusting your search or filter')
        : t('add_first_team_member', {}, 'Add your first team member');

    return (
        <div
            className="rl-wrap"
            dir={isRTL ? 'rtl' : 'ltr'}
            data-testid="roles-screen"
        >
            {/* Segment switcher — OUTSIDE SurfaceStates (see file header for
                why this is a genuine sibling-view switch, not a categorical
                filter). */}
            <div className="rl-seg">
                <SegmentedControl
                    options={segOptions}
                    value={segment}
                    onChange={setSegment}
                />
            </div>

            {/* Hero — Roles tab only. OUTSIDE SurfaceStates so it never
                vanishes with the list. */}
            {isRoles && (
                <GlassCard veil className="rl-hero" data-testid="roles-hero">
                    <HeroStat
                        label={t('total_roles', {}, 'Total Roles')}
                        value={roles.length}
                        caption={t('n_users_assigned', { count: assignedUsersCount }, '{count} users assigned')}
                    />
                </GlassCard>
            )}

            {/* Users tab controls — search + FilterButton, both OUTSIDE
                SurfaceStates: a filter/search that matches nobody must not
                unmount the means to undo it (Plan 3 Task 2 bug). */}
            {!isRoles && (
                <>
                    <div className="rl-search-wrap">
                        <SearchInput
                            value={search}
                            onChange={setSearch}
                            placeholder={t('search_users', {}, 'Search by username or name')}
                            debounce={0}
                        />
                    </div>
                    <div className="rl-control-row">
                        <FilterButton
                            activeCount={activeUserFilterCount}
                            onPress={() => setFilterSheetOpen(true)}
                        />
                    </div>
                    {activeUserFilterCount > 0 && (
                        <div className="rl-filtersum">
                            <span className="rl-filtersum-text">{activeUserFilterText}</span>
                            <button
                                type="button"
                                className="rl-filtersum-clear"
                                data-testid="clear-filters"
                                onClick={clearUserFilters}
                            >
                                {t('clear', {}, 'Clear')}
                            </button>
                        </div>
                    )}
                </>
            )}

            <PullToRefresh onRefresh={load} disabled={ptrDisabled}>
                <SurfaceStates
                    status={surfaceStatus}
                    onRetry={load}
                    emptyTitle={isRoles ? t('no_roles_yet', {}, 'No roles yet') : usersEmptyTitle}
                    emptyMsg={isRoles ? t('no_roles_msg', {}, 'Add your first role to get started.') : usersEmptyMsg}
                >
                    {isRoles ? (
                        <div className="rl-list" data-testid="roles-list">
                            {roles.map((role) => (
                                <React.Fragment key={role.id}>
                                    <RoleRow
                                        role={role}
                                        onPress={() => handleSelectRole(role)}
                                        onEdit={() => openEditRole(role)}
                                        onDelete={() => openDeleteRole(role)}
                                        t={t}
                                    />
                                    {role.id === selectedRoleId && (
                                        <RolePermissionEditor
                                            roleId={role.id}
                                            tenantId={tenantId}
                                            t={t}
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    ) : (
                        <>
                            <div className="rl-count-line" data-testid="users-count-line">
                                {t('n_users', { count: filteredCredentials.length }, '{count} Users')}
                            </div>
                            <div className="rl-users-list" data-testid="users-list">
                                {filteredCredentials.map((cred) => (
                                    <UserRow
                                        key={cred.id}
                                        cred={cred}
                                        roleName={roleMap[cred.role_id] || t('no_role', {}, 'No role')}
                                        isSelf={cred.username === currentUsername}
                                        deactivating={deactivatingId === cred.id}
                                        onEdit={() => openEditUser(cred)}
                                        onResetPassword={() => openResetPassword(cred.id)}
                                        onToggleActive={() => handleToggleActive(cred)}
                                        t={t}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </SurfaceStates>
            </PullToRefresh>

            {/* FAB — swapped by segment, both OUTSIDE SurfaceStates (see
                file header "FAB"). */}
            {isRoles && (
                <button
                    type="button"
                    className="rl-fab"
                    data-testid="add-role-btn"
                    aria-label={t('add_role', {}, 'Add Role')}
                    onClick={openCreateRole}
                >
                    <IconPlus />
                    {t('add_role', {}, 'Add Role')}
                </button>
            )}
            {!isRoles && (
                <button
                    type="button"
                    className="rl-fab"
                    data-testid="add-user-btn"
                    aria-label={t('add_user', {}, 'Add User')}
                    onClick={openCreateUser}
                >
                    <IconPlus />
                    {t('add_user', {}, 'Add User')}
                </button>
            )}

            {/* ── Role form sheet ── */}
            <RoleFormSheet
                open={roleForm.open}
                initial={roleForm.initial}
                onClose={closeRoleForm}
                onSaved={handleSaveRole}
                t={t}
            />

            {/* ── Delete role confirm ── */}
            <ConfirmSheet
                open={deleteConfirm.open}
                onClose={closeDeleteRole}
                onConfirm={handleDeleteRole}
                title={t('delete_role', {}, 'Delete Role')}
                message={t('delete_role_msg', {}, 'This role will be permanently deleted.')}
                confirmLabel={t('delete', {}, 'Delete')}
                destructive
                loading={deletingRole}
            />

            {/* ── User form sheet ── */}
            <UserFormSheet
                open={userForm.open}
                mode={userForm.mode}
                initial={userForm.cred}
                roles={roles}
                onClose={closeUserForm}
                onSaved={handleSaveUser}
                t={t}
            />

            {/* ── Reset password sheet ── */}
            <ResetPasswordSheet
                open={resetSheet.open}
                onClose={closeResetPassword}
                onSaved={handleResetPassword}
                t={t}
            />

            {/* ── Filter sheet — Users tab role / status ── */}
            <FilterSheet
                open={filterSheetOpen}
                onClose={() => setFilterSheetOpen(false)}
                groups={userFilterGroups}
                value={userFilterValue}
                onChange={handleUserFilterChange}
                onClear={clearUserFilters}
            />
        </div>
    );
}
